import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Trash2, Copy, Mail, X, Check, Loader2, FileText, Pencil, KeyRound, UserCheck, History, Download, StickyNote, Bell } from "lucide-react";
import api, { fmtDate, fmtDateTime, fmtMoney, formatApiErrorDetail } from "@/lib/api";

/**
 * Accountant-only page. Lists client sub-accounts, creates new ones with a
 * generated temporary password, and lets you remove them. Client uploads
 * flow into the accountant's own dashboard automatically.
 */
export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // { client, temporary_password }
  const [reviewing, setReviewing] = useState(null); // client being reviewed
  const [editing, setEditing] = useState(null); // client being edited
  const [historyFor, setHistoryFor] = useState(null); // client whose access-history is shown
  const [notesFor, setNotesFor] = useState(null); // client whose notes are shown
  const [resettingId, setResettingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [remindingId, setRemindingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data.clients);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Command-palette: ?client=X&open=notes|history opens the corresponding modal
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const cid = params.get("client");
    const which = params.get("open");
    if (!cid || !which || clients.length === 0) return;
    const c = clients.find((x) => x.id === cid);
    if (!c) return;
    if (which === "notes") setNotesFor(c);
    if (which === "history") setHistoryFor(c);
    const next = new URLSearchParams(params);
    next.delete("client");
    next.delete("open");
    setParams(next, { replace: true });
  }, [params, clients, setParams]);

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their uploads will remain in your data but they lose access.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      setClients((prev) => prev.filter((c) => c.id !== id));
      toast.success("Client removed");
    } catch (e) {
      toast.error("Could not remove client");
    }
  };

  const resetPassword = async (client) => {
    if (!window.confirm(`Reset password for ${client.business_name}? Their old password will stop working immediately.`)) return;
    setResettingId(client.id);
    try {
      const { data } = await api.post(`/clients/${client.id}/reset-password`);
      setJustCreated({ ...data, _reset: true }); // reuses the credentials modal with reset copy
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResettingId(null);
    }
  };

  const impersonate = async (client) => {
    if (!window.confirm(`Sign in as ${client.business_name}? You'll see the app exactly as they do. Return via the "Exit" bar at the top.`)) return;
    setImpersonatingId(client.id);
    try {
      await api.post(`/clients/${client.id}/impersonate`);
      // Force a full reload so the auth cookies + role picker re-hydrate
      window.location.href = "/client";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not impersonate");
      setImpersonatingId(null);
    }
  };

  const remindClient = async (client) => {
    if (!window.confirm(`Email a bank-statement upload reminder to ${client.email}?`)) return;
    setRemindingId(client.id);
    try {
      const { data } = await api.post(`/digest/clients/${client.id}/remind`);
      toast.success(`Reminder sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder");
    } finally {
      setRemindingId(null);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="clients-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-forest">Team</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
            Client accounts
          </h1>
          <p className="text-ink-light mt-2 text-sm max-w-xl">
            Give each of your clients their own login. They&apos;ll get a restricted portal to upload their bank statements — everything flows straight into your BizCashflow Pros dashboard.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="btn-forest inline-flex items-center gap-2"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={1.75} />
          Add client
        </button>
      </div>

      {loading ? (
        <div className="text-ink-lighter">Loading…</div>
      ) : clients.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} />
      ) : (
        <div className="card-surface overflow-hidden" data-testid="clients-list">
          <table className="w-full text-sm">
            <thead className="bg-paper text-xs uppercase text-ink-lighter tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Business</th>
                <th className="text-left px-5 py-3 font-medium">Contact</th>
                <th className="text-left px-5 py-3 font-medium">Uploaded</th>
                <th className="text-left px-5 py-3 font-medium">Review</th>
                <th className="text-left px-5 py-3 font-medium">Added</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-paper/50">
                  <td className="px-5 py-3">
                    <div className="font-medium flex items-center gap-2">
                      {c.business_name}
                      {c.pending_review_count > 0 && (
                        <span className="relative flex w-2.5 h-2.5" data-testid={`client-pending-dot-${c.id}`}>
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400" />
                        </span>
                      )}
                      {c.access_count > 0 && (
                        <button
                          type="button"
                          onClick={() => setHistoryFor(c)}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-ink-lighter bg-paper border border-border rounded-full px-1.5 py-0.5 hover:text-forest hover:border-forest/30"
                          title="View access history"
                          data-testid={`access-count-${c.id}`}
                        >
                          <History className="w-2.5 h-2.5" strokeWidth={2} />
                          <span className="num">{c.access_count}×</span>
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-ink-lighter">{c.name}</div>
                  </td>
                  <td className="px-5 py-3 text-ink-light">{c.email}</td>
                  <td className="px-5 py-3 num">{c.statement_count} statement{c.statement_count === 1 ? "" : "s"}</td>
                  <td className="px-5 py-3">
                    {c.pending_review_count > 0 ? (
                      <button
                        type="button"
                        onClick={() => setReviewing(c)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-full px-2.5 py-1 hover:bg-yellow-200"
                        data-testid={`review-btn-${c.id}`}
                      >
                        <span className="num">{c.pending_review_count}</span> pending
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-forest">
                        <Check className="w-3 h-3" strokeWidth={2.5} />
                        All received
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-ink-light num text-xs">{fmtDate(c.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setNotesFor(c)}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                        data-testid={`client-notes-${c.id}`}
                        title="Internal notes"
                      >
                        <StickyNote className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        onClick={() => remindClient(c)}
                        disabled={remindingId === c.id}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-ochre hover:bg-ochre/5 disabled:opacity-50"
                        data-testid={`remind-client-${c.id}`}
                        title="Email statement reminder"
                      >
                        {remindingId === c.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Bell className="w-4 h-4" strokeWidth={1.75} />}
                      </button>
                      <button
                        onClick={() => setHistoryFor(c)}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                        data-testid={`access-history-${c.id}`}
                        title="Access history"
                      >
                        <History className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        onClick={() => impersonate(c)}
                        disabled={impersonatingId === c.id}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5 disabled:opacity-50"
                        data-testid={`impersonate-${c.id}`}
                        title="View as this client"
                      >
                        {impersonatingId === c.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <UserCheck className="w-4 h-4" strokeWidth={1.75} />}
                      </button>
                      <button
                        onClick={() => setEditing(c)}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                        data-testid={`edit-client-${c.id}`}
                        title="Edit client"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        onClick={() => resetPassword(c)}
                        disabled={resettingId === c.id}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-ochre hover:bg-ochre/5 disabled:opacity-50"
                        data-testid={`reset-password-${c.id}`}
                        title="Reset password"
                      >
                        {resettingId === c.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <KeyRound className="w-4 h-4" strokeWidth={1.75} />}
                      </button>
                      <button
                        onClick={() => remove(c.id, c.business_name)}
                        className="p-1.5 rounded-md text-ink-lighter hover:text-destructive hover:bg-destructive/5"
                        data-testid={`remove-client-${c.id}`}
                        title="Remove client"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={(r) => {
            setShowCreate(false);
            setJustCreated(r);
            load();
          }}
        />
      )}
      {justCreated && (
        <CredentialsModal
          data={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}
      {reviewing && (
        <ReviewUploadsModal
          client={reviewing}
          onClose={() => setReviewing(null)}
          onReceived={() => load()}
        />
      )}
      {editing && (
        <EditClientModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {historyFor && (
        <AccessHistoryModal
          client={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
      {notesFor && (
        <ClientNotesModal
          client={notesFor}
          onClose={() => setNotesFor(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="card-surface p-10 text-center" data-testid="clients-empty">
      <div className="w-12 h-12 mx-auto rounded-full bg-forest/10 text-forest flex items-center justify-center mb-3">
        <Users className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="font-heading text-lg font-medium">No client accounts yet</div>
      <p className="text-sm text-ink-light mt-1 max-w-md mx-auto">
        Add your first client. You&apos;ll get a temporary password to share with them — they can change it on first login.
      </p>
      <button onClick={onCreate} className="btn-forest mt-4 inline-flex items-center gap-2" data-testid="empty-add-btn">
        <Plus className="w-4 h-4" strokeWidth={1.75} /> Add first client
      </button>
    </div>
  );
}

function formatUSPhone(v) {
  // Keep only digits, cap at 10
  const digits = (v || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function CreateClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ business_name: "", email: "", name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const onPhoneChange = (e) => {
    setForm({ ...form, phone: formatUSPhone(e.target.value) });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validate phone if provided — must be 10 digits when non-empty
    const digits = form.phone.replace(/\D/g, "");
    if (form.phone && digits.length !== 10) {
      toast.error("Phone must be 10 digits — (###) ###-####");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create client");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="create-client-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div>
            <div className="overline mb-1">Add client</div>
            <h3 className="font-heading text-xl font-medium">New client account</h3>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="create-client-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 pt-3 space-y-4">
          <label className="block text-sm">
            <span className="font-medium">Business name</span>
            <input
              required
              autoFocus
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="Acme Trading LLC"
              data-testid="new-client-business"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Contact email</span>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="owner@acme.co"
              data-testid="new-client-email"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Contact name <span className="text-ink-lighter font-normal">(optional)</span></span>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="Jane Owner"
              data-testid="new-client-name"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Phone number <span className="text-ink-lighter font-normal">(optional)</span></span>
            <input
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={onPhoneChange}
              maxLength={14}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="(555) 123-4567"
              data-testid="new-client-phone"
              aria-label="Phone number"
            />
            <span className="text-[11px] text-ink-lighter mt-1 block">US format · (###) ###-####</span>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="new-client-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-forest inline-flex items-center gap-2" data-testid="new-client-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Create client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReviewUploadsModal({ client, onClose, onReceived }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/statements/pending-review");
      setUploads(data.pending.filter((u) => u.uploaded_by_client_id === client.id));
    } catch (e) {
      toast.error("Could not load uploads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markReceived = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/statements/${id}/received`);
      setUploads((prev) => prev.filter((u) => u.id !== id));
      toast.success("Marked as received");
      onReceived();
    } catch (e) {
      toast.error("Could not mark received");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="review-uploads-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1">Review uploads</div>
            <h3 className="font-heading text-xl font-medium">{client.business_name}</h3>
            <p className="text-sm text-ink-light mt-1">
              Confirm you&apos;ve received each statement below. Once acknowledged, the client sees a green &ldquo;Received&rdquo; badge.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="review-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-border">
          {loading ? (
            <div className="p-6 text-ink-lighter">Loading…</div>
          ) : uploads.length === 0 ? (
            <div className="p-6 text-sm text-ink-lighter text-center">
              <Check className="w-6 h-6 text-forest mx-auto mb-2" strokeWidth={2} />
              No pending uploads. You&apos;re all caught up with {client.business_name}.
            </div>
          ) : (
            uploads.map((u) => (
              <div key={u.id} className="p-5 flex items-center gap-4" data-testid={`pending-upload-${u.id}`}>
                <div className="w-9 h-9 rounded-lg bg-yellow-100 text-yellow-700 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.filename}</div>
                  <div className="text-xs text-ink-lighter num">
                    {fmtDate(u.period_start)} – {fmtDate(u.period_end)} · {u.transaction_count} txns
                  </div>
                  <div className="text-xs text-ink-lighter num mt-0.5">
                    <span className="text-forest">+{fmtMoney(u.total_inflow)}</span>
                    <span className="mx-1">·</span>
                    <span className="text-ochre">-{fmtMoney(u.total_outflow)}</span>
                    <span className="mx-1">·</span>
                    Uploaded {fmtDate(u.uploaded_at)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => markReceived(u.id)}
                  disabled={busyId === u.id}
                  className="btn-forest inline-flex items-center gap-1.5 text-sm shrink-0"
                  data-testid={`mark-received-${u.id}`}
                >
                  {busyId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2} />}
                  Documents Received
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    business_name: client.business_name || "",
    email: client.email || "",
    name: client.name || "",
    phone: client.phone || "",
  });
  const [saving, setSaving] = useState(false);

  const onPhoneChange = (e) => {
    setForm({ ...form, phone: formatUSPhone(e.target.value) });
  };

  const submit = async (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, "");
    if (form.phone && digits.length !== 10) {
      toast.error("Phone must be 10 digits — (###) ###-####");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, form);
      toast.success("Client updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update client");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="edit-client-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div>
            <div className="overline mb-1">Edit client</div>
            <h3 className="font-heading text-xl font-medium">Update details</h3>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="edit-client-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 pt-3 space-y-4">
          <label className="block text-sm">
            <span className="font-medium">Business name</span>
            <input
              required
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              data-testid="edit-client-business"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Contact email</span>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              data-testid="edit-client-email"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Contact name <span className="text-ink-lighter font-normal">(optional)</span></span>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              data-testid="edit-client-name"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Phone number <span className="text-ink-lighter font-normal">(optional)</span></span>
            <input
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={onPhoneChange}
              maxLength={14}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2 font-mono focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              placeholder="(555) 123-4567"
              data-testid="edit-client-phone"
            />
            <span className="text-[11px] text-ink-lighter mt-1 block">US format · (###) ###-####</span>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="edit-client-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-forest inline-flex items-center gap-2" data-testid="edit-client-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CredentialsModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const { client, temporary_password } = data;
  const isReset = !!data._reset; // set by callers who want the reset wording
  const heading = isReset ? "Password reset" : "Client created";
  const body = isReset
    ? "Share the new temporary password with the client. Their previous password no longer works."
    : `Share these credentials with ${client.business_name}. This password is shown once — copy it now.`;
  const shareText = `${isReset ? "Your password has been reset." : "You've been added to BizCashflow Pros."} Sign in at ${window.location.origin}/login\nEmail: ${client.email}\nTemporary password: ${temporary_password}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the text and copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="credentials-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="w-9 h-9 rounded-lg bg-forest text-white flex items-center justify-center mb-3">
            <Check className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="font-heading text-xl font-medium">{heading}</div>
          <p className="text-sm text-ink-light mt-1">
            {body}
          </p>
          <div className="mt-4 p-4 bg-paper border border-border rounded-lg space-y-2 text-sm">
            <div><span className="text-ink-light">Login URL:</span> <span className="font-mono text-xs break-all">{window.location.origin}/login</span></div>
            <div><span className="text-ink-light">Email:</span> <span className="font-mono">{client.email}</span></div>
            <div>
              <span className="text-ink-light">Temp password:</span>{" "}
              <span className="font-mono font-medium text-ink" data-testid="temp-password">{temporary_password}</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <a
              className="btn-secondary inline-flex items-center gap-2"
              href={`mailto:${client.email}?subject=${encodeURIComponent("You've been added to BizCashflow Pros")}&body=${encodeURIComponent(shareText)}`}
              data-testid="email-credentials"
            >
              <Mail className="w-4 h-4" strokeWidth={1.75} />
              Email
            </a>
            <button onClick={copy} className="btn-forest inline-flex items-center gap-2" data-testid="copy-credentials">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function AccessHistoryModal({ client, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${client.id}/access-history`);
        if (!cancel) setRows(data.history || []);
      } catch (e) {
        if (!cancel) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load access history");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [client.id]);

  const durationLabel = (started, ended) => {
    if (!started || !ended) return null;
    const ms = new Date(ended) - new Date(started);
    if (!Number.isFinite(ms) || ms < 0) return null;
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "<1 min";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const headers = [
      "session_id", "accountant_name", "accountant_email",
      "client_business_name", "client_email",
      "started_at", "ended_at", "duration",
    ];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        r.session_id,
        r.accountant_name || "",
        r.accountant_email || "",
        r.client_business_name || "",
        r.client_email || "",
        r.started_at || "",
        r.ended_at || "",
        durationLabel(r.started_at, r.ended_at) || "",
      ].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (client.business_name || "client").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `access-history-${safeName}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="access-history-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1">Access history</div>
            <h3 className="font-heading text-xl font-medium">{client.business_name}</h3>
            <p className="text-sm text-ink-light mt-1">
              Immutable log of every time this account has been accessed via impersonation. Read-only for audit-defensible compliance.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={exportCsv}
              disabled={loading || rows.length === 0}
              className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="access-history-export-csv"
              title="Export as CSV"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
              Export CSV
            </button>
            <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="access-history-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-6 text-ink-lighter" data-testid="access-history-loading">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-lighter" data-testid="access-history-empty">
              <History className="w-6 h-6 text-ink-lighter mx-auto mb-2" strokeWidth={1.5} />
              No impersonation sessions recorded for this client yet.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="access-history-table">
              <thead className="bg-paper text-xs uppercase text-ink-lighter tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Accessed by</th>
                  <th className="text-left px-5 py-3 font-medium">Started</th>
                  <th className="text-left px-5 py-3 font-medium">Ended</th>
                  <th className="text-left px-5 py-3 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const dur = durationLabel(r.started_at, r.ended_at);
                  return (
                    <tr key={r.session_id} data-testid={`access-history-row-${r.session_id}`}>
                      <td className="px-5 py-3">
                        <div className="font-medium">{r.accountant_name || r.accountant_email}</div>
                        {r.accountant_name && <div className="text-xs text-ink-lighter">{r.accountant_email}</div>}
                      </td>
                      <td className="px-5 py-3 num text-xs text-ink-light">{fmtDateTime(r.started_at)}</td>
                      <td className="px-5 py-3 num text-xs text-ink-light">
                        {r.ended_at ? (
                          fmtDateTime(r.ended_at)
                        ) : (
                          <span className="inline-flex items-center gap-1 text-forest">
                            <span className="relative flex w-2 h-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-forest" />
                            </span>
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 num text-xs text-ink-light">{dur || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientNotesModal({ client, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/back-office/clients/${client.id}/notes`);
      setNotes(data.notes || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [client.id]);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/back-office/clients/${client.id}/notes`, { content });
      setNotes((prev) => [data.note, ...prev]);
      setContent("");
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/back-office/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditContent(n.content); };
  const cancelEdit = () => { setEditingId(null); setEditContent(""); };

  const saveEdit = async (id) => {
    if (!editContent.trim()) return;
    try {
      const { data } = await api.patch(`/back-office/notes/${id}`, { content: editContent });
      setNotes((prev) => prev.map((n) => n.id === id ? data.note : n));
      cancelEdit();
      toast.success("Note updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="client-notes-modal"
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1 inline-flex items-center gap-1.5"><StickyNote className="w-3 h-3" /> Internal notes</div>
            <h3 className="font-heading text-xl font-medium">{client.business_name}</h3>
            <p className="text-sm text-ink-light mt-1">
              Private to your firm. The client never sees these.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink" data-testid="client-notes-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={add} className="p-4 border-b border-border">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder="Add a note (e.g. 'Owner prefers Wed calls', 'Watch S-corp election in Q4'…)"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
            data-testid="new-note-input"
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={saving || !content.trim()}
              className="btn-forest text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="new-note-save"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add note
            </button>
          </div>
        </form>
        <div className="overflow-y-auto flex-1 divide-y divide-border">
          {loading ? (
            <div className="p-6 text-ink-lighter">Loading…</div>
          ) : notes.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-lighter" data-testid="client-notes-empty">
              <StickyNote className="w-6 h-6 text-ink-lighter mx-auto mb-2" strokeWidth={1.5} />
              No notes yet.
            </div>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="p-4" data-testid={`note-${n.id}`}>
                {editingId === n.id ? (
                  <>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={3}
                      className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={cancelEdit} className="btn-secondary text-xs">Cancel</button>
                      <button onClick={() => saveEdit(n.id)} className="btn-forest text-xs" data-testid={`note-save-${n.id}`}>Save</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-ink whitespace-pre-wrap">{n.content}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-[10px] text-ink-lighter num">{fmtDate(n.created_at)}</div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => startEdit(n)} className="text-xs text-ink-lighter hover:text-forest" data-testid={`note-edit-${n.id}`}>
                          Edit
                        </button>
                        <button onClick={() => remove(n.id)} className="text-xs text-ink-lighter hover:text-destructive" data-testid={`note-delete-${n.id}`}>
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

